// app/components/BreadcrumbTrail.tsx

"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import type { Route } from "next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  hasMandarinProgrammeBreadcrumb,
  mandarinProgrammeBreadcrumbs,
} from "@/lib/i18n/programme-breadcrumbs";
import { mandarinCareersBreadcrumbs } from "@/lib/i18n/careers-breadcrumbs";
import { buildBreadcrumbs } from "@/lib/breadcrumb";

export default function BreadcrumbTrail() {
  const pathname = usePathname(); // "/blog/ip-physics-tuition/ip-physics-crash-course" | null
  if (!pathname) return null;

  const publicPath =
    pathname
      .split(/[?#]/)[0]
      .replace(/^\/en(?=\/|$)/, "")
      .replace(/^\/zh-Hans(?=\/|$)/, "/zh") || "/";
  if (publicPath === "/") return null;
  const isMandarin = publicPath.startsWith("/zh/");
  // Do not dispatch unrelated or dynamic native pages into the finite programme cohort.
  // Their breadcrumbs remain pending alongside their untranslated page content.
  const isCareers =
    publicPath === "/zh/careers" || publicPath.startsWith("/zh/careers/");
  if (isMandarin && !isCareers && !hasMandarinProgrammeBreadcrumb(publicPath))
    return null;
  const crumbs = isMandarin
    ? publicPath === "/zh/careers" || publicPath.startsWith("/zh/careers/")
      ? mandarinCareersBreadcrumbs(publicPath)
      : mandarinProgrammeBreadcrumbs(publicPath)
    : buildBreadcrumbs(publicPath);
  const isScholarshipMatcher = publicPath === "/blog/scholarships/matcher";
  const isBlogIndex = publicPath === "/blog" || publicPath === "/zh/blog";
  // mb-6 shows page background below the trail. Where the first block has its
  // own background, mark it data-breadcrumb-flush-hero="true" (app/globals.css)
  // so it meets the trail; see docs/runbooks/ops/ui-loading-and-landmarks.md.
  return (
    <nav
      aria-label={isMandarin ? "面包屑导航" : "Breadcrumb"}
      data-breadcrumb-trail
      className={`${
        isBlogIndex ? "relative mb-0" : "sticky top-0 mb-6"
      } z-30 border-b border-blue-100/60 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80`}
    >
      <div className="mx-auto max-w-5xl px-4 py-2">
        {isScholarshipMatcher ? (
          <Link
            href="/blog/scholarships"
            prefetch={false}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-full px-2 text-sm font-semibold text-blue-800 transition hover:bg-blue-50 hover:text-blue-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 sm:hidden"
          >
            <ChevronLeft aria-hidden className="h-4 w-4" strokeWidth={2.4} />
            Scholarships
          </Link>
        ) : null}
        <ol
          className={`flex flex-wrap items-center gap-2 text-xs font-medium text-blue-800 sm:text-sm ${
            isScholarshipMatcher ? "hidden sm:flex" : ""
          }`}
        >
          {crumbs.map((c, i) => (
            <li key={c.href} className="flex items-center gap-2">
              {i > 0 && (
                <ChevronRight
                  aria-hidden
                  className="h-3.5 w-3.5 text-blue-300 sm:h-4 sm:w-4"
                  strokeWidth={2.4}
                />
              )}
              {i === crumbs.length - 1 ? (
                <span
                  aria-current="page"
                  className="inline-flex items-center rounded-full bg-blue-600/10 px-3 py-1 font-semibold text-blue-900"
                  title={c.name}
                >
                  {c.name}
                </span>
              ) : (
                <Link<string>
                  href={c.href as Route}
                  // Prefetching the heavy /blog bundle can time out after Next dev evicts the page.
                  prefetch={false}
                  className="inline-flex items-center rounded-full bg-white px-3 py-1 text-blue-700 shadow-sm ring-1 ring-blue-100 transition hover:bg-blue-50 hover:text-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                  title={c.name}
                >
                  {c.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
